import { useMemo } from "react";

import type { TransientInputFileGroup } from "../lib/groupInputFiles";

import { resultFileKey } from "../lib/formatJobResults";
import { lastPathSegment } from "../lib/storedDirectory";
import { useCheckboxGroup } from "../schedules/hooks/useCheckboxGroup";

const fileKey = (folderName: string, file: { name: string; path?: string; relativePath: string }) =>
  resultFileKey(folderName || "inputs", file.path || file.relativePath || file.name);

const zipFolderName = (file: { relativePath: string }) => {
  const parts = file.relativePath.split("/").filter(Boolean);
  return parts.slice(0, -1).join("/") || "inputs";
};

export const useInputSelection = (groups: TransientInputFileGroup[]) => {
  const fileKeys = useMemo(
    () => groups.flatMap((group) => group.files.map((file) => fileKey(group.folderName, file))),
    [groups]
  );
  const selection = useCheckboxGroup(fileKeys, []);
  const selected = selection.selected;
  const selectedFiles = useMemo(
    () =>
      groups.flatMap((group) =>
        group.files.flatMap((file) =>
          selected.has(fileKey(group.folderName, file))
            ? [
                {
                  fileName: lastPathSegment(file.name),
                  folderName: zipFolderName(file),
                  path: file.path,
                },
              ]
            : []
        )
      ),
    [groups, selected]
  );

  return {
    isAllSelected: fileKeys.length > 0 && selection.isAllSelected,
    isSelected: selection.isSelected,
    selectedFiles,
    setMany: selection.setMany,
    toggle: selection.toggle,
    toggleAll: selection.toggleAll,
  };
};
